/**
 * Helpers for GSTIN registration/cancellation date checks against compliance periods.
 *
 * GSTIN object shape:
 *   { number, category, registration_date?: "YYYY-MM-DD", cancellation_date?: "YYYY-MM-DD" }
 *
 * Period strings come in several formats produced by the backend:
 *   - Monthly:    "YYYY-MM"  (e.g. "2026-04")  OR  "<mon>_YYYY"  (e.g. "apr_2026")
 *   - Quarterly:  "Q1_YYYY"  /  "Q1"           (Apr-Jun, Jul-Sep, Oct-Dec, Jan-Mar of next year)
 *   - Half-yearly:"H1_YYYY" / "H2_YYYY"        (Apr-Sep, Oct-Mar)
 *   - Annual:     "YYYY"     OR  "FY_YYYY"      (FY Apr Y → Mar Y+1)
 */

const MONTH_MAP = {
  jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
  jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
};

const lastDayOfMonth = (year, month) => new Date(year, month, 0).getDate();

const fmt = (year, month, day) =>
  `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

/**
 * Returns [startDateISO, endDateISO] (YYYY-MM-DD) for a period+frequency, or null if unparseable.
 */
export const getPeriodDateRange = (period, frequency) => {
  if (!period || period === '-') return null;
  const freq = (frequency || 'monthly').toLowerCase();

  // Format 1: "YYYY-MM"  → monthly
  let m = /^(\d{4})-(\d{2})$/.exec(period);
  if (m) {
    const y = parseInt(m[1], 10);
    const mo = parseInt(m[2], 10);
    return [fmt(y, mo, 1), fmt(y, mo, lastDayOfMonth(y, mo))];
  }

  // Format 2: "<mon>_YYYY"  → monthly (e.g. apr_2026)
  m = /^([a-z]{3})_(\d{4})$/i.exec(period);
  if (m) {
    const mo = MONTH_MAP[m[1].toLowerCase()];
    const y = parseInt(m[2], 10);
    if (mo) return [fmt(y, mo, 1), fmt(y, mo, lastDayOfMonth(y, mo))];
  }

  // Format 3: "Q1" / "Q2" / "Q3" / "Q4" optionally suffixed with _YYYY (Indian FY: Q1=Apr-Jun)
  m = /^Q([1-4])(?:_?(\d{4}))?$/i.exec(period);
  if (m) {
    const q = parseInt(m[1], 10);
    const y = m[2] ? parseInt(m[2], 10) : null;
    if (y) {
      const startMonth = (q - 1) * 3 + 4; // Q1=4, Q2=7, Q3=10, Q4=13(=Jan next yr)
      const sy = startMonth > 12 ? y + 1 : y;
      const sm = ((startMonth - 1) % 12) + 1;
      const em = sm + 2 > 12 ? sm + 2 - 12 : sm + 2;
      const ey = sm + 2 > 12 ? sy + 1 : sy;
      return [fmt(sy, sm, 1), fmt(ey, em, lastDayOfMonth(ey, em))];
    }
  }

  // Format 4: "H1_YYYY" / "H2_YYYY" (FY half-yearly: H1=Apr-Sep, H2=Oct-Mar)
  m = /^H([12])_?(\d{4})$/i.exec(period);
  if (m) {
    const h = parseInt(m[1], 10);
    const y = parseInt(m[2], 10);
    if (h === 1) return [fmt(y, 4, 1), fmt(y, 9, 30)];
    return [fmt(y, 10, 1), fmt(y + 1, 3, 31)];
  }

  // Format 5: "FY_YYYY" or "FY YYYY-YY" → annual (Apr Y → Mar Y+1)
  m = /^FY[_\s]?(\d{4})/i.exec(period);
  if (m) {
    const y = parseInt(m[1], 10);
    return [fmt(y, 4, 1), fmt(y + 1, 3, 31)];
  }

  // Format 6: bare "YYYY" — interpret as Indian FY when frequency is annual
  m = /^(\d{4})$/.exec(period);
  if (m) {
    const y = parseInt(m[1], 10);
    if (freq === 'annual' || freq === 'half_yearly') {
      return [fmt(y, 4, 1), fmt(y + 1, 3, 31)];
    }
  }

  return null;
};

/**
 * Returns true if the given period is active for this GSTIN.
 * Inactive when:
 *   - period_start  >  cancellation_date  (cancelled before period began)
 *   - period_end    <  registration_date   (registered after period ended)
 */
export const isPeriodActiveForGstin = (period, frequency, gstin) => {
  if (!gstin || (!gstin.cancellation_date && !gstin.registration_date)) return true;
  const range = getPeriodDateRange(period, frequency);
  if (!range) return true; // unparseable → don't block
  const [pstart, pend] = range;
  if (gstin.cancellation_date && pstart > gstin.cancellation_date) return false;
  if (gstin.registration_date && pend < gstin.registration_date) return false;
  return true;
};

/**
 * Build a tooltip for an inactive cell.
 */
export const inactiveReason = (period, frequency, gstin) => {
  if (!gstin) return '';
  const range = getPeriodDateRange(period, frequency);
  if (!range) return '';
  const [pstart, pend] = range;
  if (gstin.cancellation_date && pstart > gstin.cancellation_date) {
    return `GSTIN cancelled on ${formatDateDisplay(gstin.cancellation_date)}`;
  }
  if (gstin.registration_date && pend < gstin.registration_date) {
    return `GSTIN registered on ${formatDateDisplay(gstin.registration_date)}`;
  }
  return '';
};

const formatDateDisplay = (iso) => {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${parseInt(m[3], 10)}-${months[parseInt(m[2], 10) - 1]}-${m[1].slice(2)}`;
};
